import { Component, inject, ViewChild, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize, first } from 'rxjs/operators';
import { SignaturePadComponent } from 'src/app/shared/components/signature-pad/signature-pad.component';
import { SignAnnexRequest } from 'src/app/contracts/requests/sign-annex-request';
import { AnnexService } from 'src/app/data/annex.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { ErrorCode } from 'src/app/common/error-codes/ErrorCode';
import { ApiResult } from 'src/app/models/ApiResult';
import { HttpErrorResponse } from '@angular/common/http';
import { SignatureMethodDto } from 'src/app/models/dtos/annexes/signature-method-dto';

export enum SignatureMethod {
  Electronic = 1,
  SMS = 2,
  Qualified = 3
}

export interface SignatureModalData {
  annexId: number;
  workerId: number;
  isEmployerRepresentative: boolean;
  allowedMethods: SignatureMethodDto[];
}

@Component({
  selector: 'app-signature-modal',
  templateUrl: './signature-method-modal.component.html',
  styleUrls: ['./signature-method-modal.component.scss'],
  standalone: false,
})

export class SignatureMethodModalComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<SignatureMethodModalComponent>>(MatDialogRef);
  private annexService = inject(AnnexService);
  private snackbarService = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private formBuilder = inject(FormBuilder);
  data = inject<SignatureModalData>(MAT_DIALOG_DATA);

  @ViewChild(SignaturePadComponent) signaturePad: SignaturePadComponent;

  signatureMethod = SignatureMethod;
  selectedMethod: SignatureMethod | null = null;
  verificationCodeFormGroup: FormGroup<{ verificationCode: FormControl<string | null> }>;
  isVerificationCodeSent: boolean = false;

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.verificationCodeFormGroup = this.formBuilder.group({
      verificationCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    });
  }

  get verificationCode(): FormControl<string | null> {
    return this.verificationCodeFormGroup.controls.verificationCode;
  }

  get isSignaturePadEmpty(): boolean {
    return this.signaturePad?.isEmpty ?? true;
  }

  get isSignatureValid(): boolean {
    return this.signaturePad?.isValid ?? false;
  }

  get isVerifyButtonEnabled(): boolean {
    return this.verificationCode.valid;
  }

  get smsAllowed(): SignatureMethodDto {
    return this.data.allowedMethods.find(s => s.Id === SignatureMethod.SMS);
  }

  get electronicAllowed(): SignatureMethodDto {
    return this.data.allowedMethods.find(s => s.Id === SignatureMethod.Electronic);
  }

  onSelectMethod(method: SignatureMethod): void {
    this.selectedMethod = method;
    if (method === SignatureMethod.SMS) {
      this.sendVerificationCode();
    }
  }

  onBackToMethodSelection(): void {
    this.selectedMethod = null;
    this.isVerificationCodeSent = false;
    this.verificationCodeFormGroup.reset();
  }

  onClearSignaturePadClick(): void {
    this.signaturePad?.clear();
  }

  onSignWithPadClick(): void {
    if (!this.isSignatureValid) {
      return;
    }

    this.spinner.show();
    
    const signatureBase64 = this.signaturePad.signaturePng;
    const request: SignAnnexRequest = { SignatureFileBase64: signatureBase64 };

    this.annexService.submitSignature(this.data.annexId, request)
      .pipe(
        first(),
        finalize(() => this.spinner.hide())
      )
      .subscribe({
        next: () => {
          this.dialogRef.close({ success: true, method: this.selectedMethod });
        },
        error: () => {
          this.snackbarService.openErrorSnackBar(ErrorCode.SomethingWentWrong);
        },
      });
  }

  onSendVerificationCodeClick(): void {
    this.sendVerificationCode();
  }

  sendVerificationCode(): void {
    this.spinner.show();
    this.annexService
      .generateAnnexApprovalCode({
        AnnexId: this.data.annexId,
      })
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe({
        next: () => {
          this.isVerificationCodeSent = true;
          this.snackbarService.openSuccessSnackBar('Annex.VerificationCodeSent');
        },
        error: (error: HttpErrorResponse) => {
          this.snackbarService.openErrorSnackBar(error.error);
        }
      });
  }

  onVerifyCodeClick(): void {
    if (!this.isVerifyButtonEnabled) {
      return;
    }

    this.spinner.show();
    this.annexService
      .verifyAnnexApprovalCode({
        AnnexId: this.data.annexId,
        VerificationCode: this.verificationCode.value,
        SignatureMethodId: this.selectedMethod,
      })
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe({
        next: () => {
          this.dialogRef.close({ success: true, method: this.selectedMethod });
        },
        error: (response: HttpErrorResponse) => {
          this.snackbarService.openErrorSnackBar(response.error || ErrorCode.SomethingWentWrong);
        },
      });
  }


  onCancel(): void {
    this.dialogRef.close(null);
  }
}
